import type { IndustryId } from './types'

/**
 * The behaviour behind an industry choice.
 *
 * Until now picking a sector only moved the price. These rules are what actually differ
 * between a clinic and a marketplace: which messages become tickets, how fast someone must
 * reply, and what the resulting ticket is called. They are seeded into the customer's own
 * database at provision time and are editable there, so a customer can diverge from the
 * sector default without a code change on our side.
 */
export interface WorkflowRuleSeed {
  name: string
  /** Lower runs first; the first matching rule wins. */
  priorityOrder: number
  /** Matched case-insensitively as substrings against the message body. */
  keywords: string[]
  /** Restricts the rule to group or direct chats; null means both. */
  appliesTo: 'direct' | 'group' | null
  category: string
  ticketPriority: 'low' | 'normal' | 'high' | 'urgent'
  /** Minutes before the ticket counts as breached. */
  slaMinutes: number
  autoTags: string[]
}

/**
 * The rule applied when nothing more specific matches, so every inbound message is still
 * accounted for rather than silently ignored.
 */
export const FALLBACK_RULE: WorkflowRuleSeed = {
  name: 'General enquiry',
  priorityOrder: 999,
  keywords: [],
  appliesTo: null,
  category: 'General',
  ticketPriority: 'normal',
  slaMinutes: 480,
  autoTags: [],
}

const HOUR = 60

export const INDUSTRY_WORKFLOWS: Record<IndustryId, WorkflowRuleSeed[]> = {
  general: [
    {
      name: 'Escalation',
      priorityOrder: 10,
      keywords: ['urgent', 'asap', 'escalate', 'complaint', 'not working'],
      appliesTo: null,
      category: 'Escalation',
      ticketPriority: 'urgent',
      slaMinutes: 30,
      autoTags: ['escalation'],
    },
    {
      name: 'Sales enquiry',
      priorityOrder: 20,
      keywords: ['price', 'pricing', 'quote', 'demo', 'interested'],
      appliesTo: null,
      category: 'Sales',
      ticketPriority: 'high',
      slaMinutes: 2 * HOUR,
      autoTags: ['lead'],
    },
    FALLBACK_RULE,
  ],

  ecommerce: [
    {
      name: 'Delivery problem',
      priorityOrder: 10,
      keywords: ['not delivered', 'delayed', 'missing', 'wrong item', 'damaged', 'where is my order'],
      appliesTo: null,
      category: 'Delivery',
      ticketPriority: 'urgent',
      // Delivery complaints turn into chargebacks and public reviews quickly.
      slaMinutes: 30,
      autoTags: ['delivery', 'at-risk'],
    },
    {
      name: 'Refund or return',
      priorityOrder: 20,
      keywords: ['refund', 'return', 'exchange', 'cancel order', 'money back'],
      appliesTo: null,
      category: 'Refunds',
      ticketPriority: 'high',
      slaMinutes: 2 * HOUR,
      autoTags: ['refund'],
    },
    {
      name: 'Order status',
      priorityOrder: 30,
      keywords: ['order', 'tracking', 'shipped', 'dispatch', 'invoice'],
      appliesTo: null,
      category: 'Order status',
      ticketPriority: 'normal',
      slaMinutes: 4 * HOUR,
      autoTags: ['order'],
    },
    FALLBACK_RULE,
  ],

  healthcare: [
    {
      name: 'Possible emergency',
      priorityOrder: 1,
      keywords: ['emergency', 'severe', 'bleeding', 'chest pain', 'ambulance', 'urgent', 'breathing'],
      appliesTo: null,
      category: 'Clinical urgent',
      ticketPriority: 'urgent',
      // Deliberately the tightest target in the product. A missed clinical message is not
      // an inconvenience, and this rule runs before every other healthcare rule.
      slaMinutes: 10,
      autoTags: ['clinical', 'urgent'],
    },
    {
      name: 'Prescription request',
      priorityOrder: 10,
      keywords: ['prescription', 'refill', 'medication', 'dosage', 'repeat'],
      appliesTo: null,
      category: 'Prescriptions',
      ticketPriority: 'high',
      slaMinutes: 2 * HOUR,
      autoTags: ['clinical'],
    },
    {
      name: 'Appointment',
      priorityOrder: 20,
      keywords: ['appointment', 'reschedule', 'booking', 'cancel', 'slot'],
      appliesTo: null,
      category: 'Appointments',
      ticketPriority: 'normal',
      slaMinutes: 4 * HOUR,
      autoTags: ['scheduling'],
    },
    {
      name: 'Billing and insurance',
      priorityOrder: 30,
      keywords: ['bill', 'insurance', 'claim', 'invoice', 'payment'],
      appliesTo: null,
      category: 'Billing',
      ticketPriority: 'normal',
      slaMinutes: 8 * HOUR,
      autoTags: ['billing'],
    },
    FALLBACK_RULE,
  ],

  education: [
    {
      name: 'Safeguarding concern',
      priorityOrder: 1,
      keywords: ['bullying', 'unsafe', 'incident', 'missing', 'harassment'],
      appliesTo: null,
      category: 'Safeguarding',
      ticketPriority: 'urgent',
      slaMinutes: 15,
      autoTags: ['safeguarding'],
    },
    {
      name: 'Admission enquiry',
      priorityOrder: 10,
      keywords: ['admission', 'enrol', 'enroll', 'apply', 'fees', 'seat'],
      appliesTo: null,
      category: 'Admissions',
      ticketPriority: 'high',
      slaMinutes: 2 * HOUR,
      autoTags: ['admissions', 'lead'],
    },
    {
      name: 'Parent query in cohort group',
      priorityOrder: 20,
      keywords: ['homework', 'timetable', 'exam', 'result', 'attendance'],
      // Cohort groups are where questions get lost in the scroll, so these are pulled out
      // as tickets even though the same words in a 1:1 chat are routine.
      appliesTo: 'group',
      category: 'Academic',
      ticketPriority: 'normal',
      slaMinutes: 6 * HOUR,
      autoTags: ['academic'],
    },
    FALLBACK_RULE,
  ],

  financial: [
    {
      name: 'Suspected fraud',
      priorityOrder: 1,
      keywords: ['fraud', 'unauthorised', 'unauthorized', 'stolen', 'hacked', 'scam', 'block card'],
      appliesTo: null,
      category: 'Fraud',
      ticketPriority: 'urgent',
      slaMinutes: 15,
      autoTags: ['fraud', 'regulated'],
    },
    {
      name: 'Complaint',
      priorityOrder: 5,
      // Regulators count the clock from first contact, so complaints are tagged on arrival
      // rather than when an agent happens to recognise one.
      keywords: ['complaint', 'ombudsman', 'mis-sold', 'mis sold', 'escalate'],
      appliesTo: null,
      category: 'Complaints',
      ticketPriority: 'urgent',
      slaMinutes: 30,
      autoTags: ['complaint', 'regulated'],
    },
    {
      name: 'Account servicing',
      priorityOrder: 20,
      keywords: ['statement', 'balance', 'transfer', 'kyc', 'document', 'limit'],
      appliesTo: null,
      category: 'Servicing',
      ticketPriority: 'normal',
      slaMinutes: 4 * HOUR,
      autoTags: ['servicing'],
    },
    FALLBACK_RULE,
  ],

  field_services: [
    {
      name: 'Site safety incident',
      priorityOrder: 1,
      keywords: ['accident', 'injury', 'unsafe', 'gas leak', 'fire', 'electric shock', 'emergency'],
      appliesTo: null,
      category: 'Safety',
      ticketPriority: 'urgent',
      slaMinutes: 10,
      autoTags: ['safety', 'incident'],
    },
    {
      name: 'Technician blocked on site',
      priorityOrder: 10,
      keywords: ['stuck', 'blocked', 'no access', 'part missing', 'wrong part', 'cannot complete'],
      // Crews report from dispatch groups, which is exactly where a blocked job gets lost
      // in the scroll while a customer waits at the site.
      appliesTo: 'group',
      category: 'Dispatch',
      ticketPriority: 'urgent',
      slaMinutes: 30,
      autoTags: ['dispatch', 'blocked'],
    },
    {
      name: 'Job scheduling',
      priorityOrder: 20,
      keywords: ['reschedule', 'eta', 'running late', 'slot', 'appointment', 'visit'],
      appliesTo: null,
      category: 'Scheduling',
      ticketPriority: 'high',
      slaMinutes: HOUR,
      autoTags: ['scheduling'],
    },
    {
      name: 'Quote or job request',
      priorityOrder: 30,
      keywords: ['quote', 'estimate', 'book', 'repair', 'install', 'service'],
      appliesTo: null,
      category: 'Jobs',
      ticketPriority: 'normal',
      slaMinutes: 4 * HOUR,
      autoTags: ['job'],
    },
    FALLBACK_RULE,
  ],

  logistics: [
    {
      // A driver in trouble is the one case where the person messaging may be in danger,
      // so it outranks everything and carries the tightest SLA in the catalog.
      name: 'Vehicle or driver incident',
      priorityOrder: 1,
      keywords: ['accident', 'breakdown', 'stranded', 'police', 'theft', 'stolen', 'injured'],
      appliesTo: null,
      category: 'Incident',
      ticketPriority: 'urgent',
      slaMinutes: 15,
      autoTags: ['incident', 'safety'],
    },
    {
      name: 'Delivery exception',
      priorityOrder: 10,
      keywords: ['damaged', 'missing', 'wrong address', 'refused', 'failed delivery', 'undelivered'],
      appliesTo: null,
      category: 'Exception',
      ticketPriority: 'high',
      slaMinutes: HOUR,
      autoTags: ['exception'],
    },
    {
      name: 'Route or ETA change',
      priorityOrder: 20,
      keywords: ['delayed', 'eta', 'reroute', 'traffic', 'running late', 'reschedule'],
      appliesTo: null,
      category: 'Scheduling',
      ticketPriority: 'normal',
      slaMinutes: 4 * HOUR,
      autoTags: ['scheduling'],
    },
    {
      // Chased in bulk at shift end rather than answered live, so it sits below a full day.
      name: 'Proof of delivery request',
      priorityOrder: 30,
      keywords: ['pod', 'proof of delivery', 'signature', 'receipt', 'docket'],
      appliesTo: null,
      category: 'Documentation',
      ticketPriority: 'low',
      slaMinutes: 8 * HOUR,
      autoTags: ['pod'],
    },
    FALLBACK_RULE,
  ],
}

export interface RuleMatchInput {
  body: string | null
  kind: 'direct' | 'group'
}

export interface EvaluatedRule {
  rule: {
    name: string
    category: string
    ticketPriority: 'low' | 'normal' | 'high' | 'urgent'
    slaMinutes: number
    autoTags: string[]
    keywords: string[]
    appliesTo: 'direct' | 'group' | null
    priorityOrder: number
    enabled?: boolean
  }
  matchedKeyword: string | null
}

/**
 * Picks the rule that governs a message. Rules are sorted by their configured order and the
 * first match wins, so a clinic's emergency rule cannot be shadowed by a broader one.
 * A keywordless rule matches anything, which is how the fallback catches the remainder.
 */
export function evaluateRules<T extends EvaluatedRule['rule']>(
  rules: T[],
  input: RuleMatchInput,
): { rule: T; matchedKeyword: string | null } | null {
  const body = (input.body ?? '').toLowerCase()
  const ordered = [...rules]
    .filter((rule) => rule.enabled !== false)
    .sort((a, b) => a.priorityOrder - b.priorityOrder)

  for (const rule of ordered) {
    if (rule.appliesTo && rule.appliesTo !== input.kind) continue

    if (rule.keywords.length === 0) return { rule, matchedKeyword: null }

    const hit = rule.keywords.find((keyword) => body.includes(keyword.toLowerCase()))
    if (hit) return { rule, matchedKeyword: hit }
  }

  return null
}

/** Categories a sector uses, for filters and reporting. */
export function categoriesFor(industryId: IndustryId): string[] {
  return [...new Set(INDUSTRY_WORKFLOWS[industryId].map((rule) => rule.category))]
}

/* ---------------------------------------------------------------- editing rules */

/**
 * Everything below exists because a routing rule is written once and then trusted for
 * months. The failure is never an error message — it is a rule that quietly never fires, and
 * a category of message that stops being ticketed without anyone noticing. So the editor is
 * built to answer two questions before the customer saves: what will this do, and is
 * anything here already dead?
 */

export interface RuleDraft {
  name: string
  keywords: string[]
  appliesTo: 'direct' | 'group' | null
  category: string
  ticketPriority: 'low' | 'normal' | 'high' | 'urgent'
  slaMinutes: number
  autoTags: string[]
  enabled?: boolean
}

/** Trims, lowercases and dedupes keywords. Matching is case-insensitive, so storing mixed
 *  case only makes two rules look different when they behave identically. */
export function normaliseKeywords(raw: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const entry of raw) {
    const value = entry.trim().toLowerCase()
    if (!value || seen.has(value)) continue
    seen.add(value)
    out.push(value)
  }
  return out
}

export function validateRule(draft: RuleDraft): string[] {
  const problems: string[] = []
  if (!draft.name.trim()) problems.push('Give the rule a name.')
  if (!draft.category.trim()) problems.push('Choose a category for the tickets it opens.')
  if (!Number.isFinite(draft.slaMinutes) || draft.slaMinutes <= 0) {
    problems.push('The reply target must be more than zero minutes.')
  }
  // A day and a half is already generous; anything beyond a fortnight is a typo, and a
  // mistyped target silently disables the breach reporting for that whole category.
  if (draft.slaMinutes > 20160) problems.push('That reply target is over two weeks. Check the number.')
  return problems
}

export interface RuleProblem {
  /** Index in the ordered list the problem belongs to. */
  index: number
  kind: 'unreachable' | 'duplicate-keyword' | 'no-fallback'
  message: string
}

/** Whether `earlier` matches everything `later` would, making `later` dead code. */
function shadows(earlier: EvaluatedRule['rule'], later: EvaluatedRule['rule']): boolean {
  // A rule restricted to one chat kind cannot shadow one that also covers the other.
  if (earlier.appliesTo !== null && earlier.appliesTo !== later.appliesTo) return false

  // A keywordless rule matches every message it is offered.
  if (earlier.keywords.length === 0) return true
  if (later.keywords.length === 0) return false

  // Matching is substring containment, so `earlier` wins on any body that would have reached
  // `later` exactly when each of the later keywords already contains an earlier one.
  return later.keywords.every((keyword) =>
    earlier.keywords.some((candidate) => keyword.toLowerCase().includes(candidate.toLowerCase())),
  )
}

/**
 * Finds rules that can never fire, and keywords claimed twice.
 *
 * The commonest mistake by far is adding a specific rule below the catch-all: it looks right
 * in the list, saves without complaint, and never runs. Reporting it costs nothing and is the
 * difference between an editor and a trap.
 */
export function findRuleProblems(rules: EvaluatedRule['rule'][]): RuleProblem[] {
  const ordered = [...rules].sort((a, b) => a.priorityOrder - b.priorityOrder)
  const problems: RuleProblem[] = []

  ordered.forEach((rule, index) => {
    if (rule.enabled === false) return

    for (let before = 0; before < index; before++) {
      const earlier = ordered[before]
      if (earlier.enabled === false) continue
      if (!shadows(earlier, rule)) continue

      problems.push({
        index,
        kind: 'unreachable',
        message:
          earlier.keywords.length === 0
            ? `“${earlier.name}” matches everything and runs first, so this rule never will. Move it above “${earlier.name}”.`
            : `“${earlier.name}” already catches every message this would, so this rule never runs.`,
      })
      break
    }
  })

  // The same keyword in two rules is not an error, but the later rule's author almost
  // certainly did not know the earlier one would win.
  const claimed = new Map<string, string>()
  ordered.forEach((rule, index) => {
    if (rule.enabled === false) return
    for (const keyword of rule.keywords) {
      const key = keyword.toLowerCase()
      const owner = claimed.get(key)
      if (owner && owner !== rule.name) {
        problems.push({
          index,
          kind: 'duplicate-keyword',
          message: `“${keyword}” is already used by “${owner}”, which runs first.`,
        })
      } else if (!owner) {
        claimed.set(key, rule.name)
      }
    }
  })

  // Without a catch-all, a message matching nothing is never ticketed and never counted.
  const hasFallback = ordered.some(
    (rule) => rule.enabled !== false && rule.keywords.length === 0 && rule.appliesTo === null,
  )
  if (!hasFallback && ordered.length > 0) {
    problems.push({
      index: ordered.length - 1,
      kind: 'no-fallback',
      message:
        'No rule catches everything else. Messages matching none of these will not open a ticket at all.',
    })
  }

  return problems
}

export interface RuleTrace {
  name: string
  outcome: 'matched' | 'skipped' | 'no-keyword-hit' | 'disabled'
  detail: string
}

export interface RuleExplanation {
  matched: { name: string; category: string; ticketPriority: string; slaMinutes: number; autoTags: string[] } | null
  matchedKeyword: string | null
  trace: RuleTrace[]
}

/**
 * Runs a sample message through the rules and says what happened to each one.
 *
 * The trace is the point. Being told which rule won answers half the question; the other half
 * — why the rule you expected did not — is what someone actually needs when a message was
 * filed under the wrong category.
 */
export function explainMatch(
  rules: EvaluatedRule['rule'][],
  input: RuleMatchInput,
): RuleExplanation {
  const ordered = [...rules].sort((a, b) => a.priorityOrder - b.priorityOrder)
  const body = (input.body ?? '').toLowerCase()
  const trace: RuleTrace[] = []
  let matched: RuleExplanation['matched'] = null
  let matchedKeyword: string | null = null

  for (const rule of ordered) {
    if (matched) break

    if (rule.enabled === false) {
      trace.push({ name: rule.name, outcome: 'disabled', detail: 'Turned off.' })
      continue
    }

    if (rule.appliesTo && rule.appliesTo !== input.kind) {
      trace.push({
        name: rule.name,
        outcome: 'skipped',
        detail: `Only applies to ${rule.appliesTo === 'group' ? 'group chats' : 'direct chats'}.`,
      })
      continue
    }

    if (rule.keywords.length === 0) {
      matched = {
        name: rule.name,
        category: rule.category,
        ticketPriority: rule.ticketPriority,
        slaMinutes: rule.slaMinutes,
        autoTags: rule.autoTags,
      }
      trace.push({ name: rule.name, outcome: 'matched', detail: 'Catches everything.' })
      continue
    }

    const hit = rule.keywords.find((keyword) => body.includes(keyword.toLowerCase()))
    if (hit) {
      matched = {
        name: rule.name,
        category: rule.category,
        ticketPriority: rule.ticketPriority,
        slaMinutes: rule.slaMinutes,
        autoTags: rule.autoTags,
      }
      matchedKeyword = hit
      trace.push({ name: rule.name, outcome: 'matched', detail: `Found “${hit}”.` })
    } else {
      trace.push({
        name: rule.name,
        outcome: 'no-keyword-hit',
        detail: `None of ${rule.keywords.length} keywords appear.`,
      })
    }
  }

  return { matched, matchedKeyword, trace }
}

/** Minutes rendered the way a person would say them, for a reply target. */
export function formatSla(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  if (minutes % 1440 === 0) {
    const days = minutes / 1440
    return `${days} ${days === 1 ? 'day' : 'days'}`
  }
  if (minutes % 60 === 0) return `${minutes / 60} h`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}
